import { multiaddr } from '@multiformats/multiaddr';

import type { LoggerService } from '@/services/abstract/LoggerService';
import type { HeliaLifecycleService } from '@/services/abstract/poc/HeliaLifecycleService';
import type { Libp2pService } from '@/services/abstract/poc/Libp2pService';
import type { MessagingService } from '@/services/abstract/poc/MessagingService';
import {
  MESH_HEARTBEAT_TOPIC,
  ORG_GLOBAL_TOPIC,
  SIX_TOPIC,
  USER_TOPIC,
} from '@/config/networkConstants';
import {
  type ConnectionUpdateCallback,
  type MessageCallback,
  type NodeMetadata,
  P2PStackManager,
  type SharedNodeOptions,
  type SharedNodeStartResult,
  type SixSubscriptionOptions,
} from '@/services/abstract/poc/P2PStackManager';
import { P2P_PIN_COUNT_TTL_MS } from '@/constants';
import type { Logger } from '@/types/Logger';
import type { HeliaNode } from '@/types/poc/helia';
import type {
  ConnectionInfo,
  GroupStatus,
  LibP2pNodeCallbacks,
  LibP2pWithServices,
} from '@/types/poc/libp2p';
import { extractConnectionInfo } from '@/services/poc/libp2p.utils';
import type { BrokerStatus, P2PDiagnostics } from '@/types/P2PDiagnostics';

// The destination peerId is the value after the LAST `/p2p/` in a multiaddr, so a
// relay address `…/p2p/RELAY/p2p-circuit/p2p/TARGET` resolves to TARGET, not the relay.
const extractDestinationPeerId = (multiaddrString: string): string | null => {
  if (!multiaddrString.includes('/p2p/')) {
    return null;
  }
  const segments = multiaddrString.split('/p2p/');
  const peerId = segments[segments.length - 1].split('/')[0];
  return peerId.length > 0 ? peerId : null;
};

const deriveBrokerStatus = (
  brokerMultiaddr: string | null,
  peers: ConnectionInfo[]
): BrokerStatus => {
  if (brokerMultiaddr === null || brokerMultiaddr.length === 0) {
    return 'unknown';
  }

  const brokerPeerId = extractDestinationPeerId(brokerMultiaddr);
  if (brokerPeerId === null) {
    return 'unknown';
  }

  const isConnected = peers.some(
    (peer) => peer.peerId === brokerPeerId || peer.address.includes(brokerPeerId)
  );
  return isConnected ? 'connected' : 'disconnected';
};

const emptyP2PDiagnostics = (): P2PDiagnostics => ({
  peerCount: 0,
  peers: [],
  multiaddrs: [],
  pinCount: null,
  pinnedCids: [],
  brokerStatus: 'unknown',
});

const syncConnectionStats = (status: GroupStatus, connections: ConnectionInfo[]): void => {
  const directCount = connections.filter((connection) => connection.isDirect).length;
  const relayCount = connections.filter((connection) => connection.isRelay).length;
  status.connectionStats.activeConnections = {
    direct: directCount,
    relay: relayCount,
    total: connections.length,
  };
};

const createInitialNodeMetadata = (bootstrapNodes: string[]): NodeMetadata => ({
  status: {
    relayConnected: false,
    subscribedTopics: [],
    subscriberCounts: new Map(),
    connections: [],
    peerId: '',
    connectionStats: {
      activeConnections: { direct: 0, relay: 0, total: 0 },
      lifetimeStats: {
        directConnections: 0,
        relayConnections: 0,
        directDisconnections: 0,
        relayDisconnections: 0,
        totalConnections: 0,
        totalDisconnections: 0,
      },
      uniquePeers: {
        direct: new Set(),
        relay: new Set(),
        total: new Set(),
      },
    },
  },
  config: { relayAddresses: bootstrapNodes },
});

export class P2PStackManagerImpl extends P2PStackManager {
  private readonly log: Logger;
  #libp2pNode: LibP2pWithServices | null = null;
  #heliaNode: HeliaNode | null = null;
  #nodeStarting: Promise<SharedNodeStartResult> | null = null;
  #nodeMetadata: NodeMetadata | null = null;
  #onMessage: MessageCallback | null = null;
  #globalTopicCleanup: (() => void) | null = null;
  #pinnedCidsCache: { value: string[]; at: number; node: HeliaNode } | null = null;
  readonly #sixSubscriptions: Map<string, () => void> = new Map<string, () => void>();
  readonly #sixConnectionCallbacks: Map<string, ConnectionUpdateCallback> = new Map<
    string,
    ConnectionUpdateCallback
  >();

  constructor(
    loggerService: LoggerService,
    private readonly libp2pService: Libp2pService,
    private readonly heliaLifecycleService: HeliaLifecycleService,
    private readonly messagingService: MessagingService
  ) {
    super();
    this.log = loggerService.createLogger({ namespace: 'six3ro:p2p:stack-manager' });
  }

  async startSharedNode(options: SharedNodeOptions): Promise<SharedNodeStartResult> {
    if (this.#libp2pNode !== null) {
      return { peerId: this.#libp2pNode.peerId.toString(), isSuccessful: true };
    }

    if (this.#nodeStarting !== null) {
      return await this.#nodeStarting;
    }

    this.#onMessage = options.onMessage;
    this.#nodeStarting = this.doStartNode(options).finally(() => {
      this.#nodeStarting = null;
    });

    return await this.#nodeStarting;
  }

  subscribeToSix(options: SixSubscriptionOptions): void {
    const { sixId, onConnectionUpdate } = options;

    if (this.#libp2pNode === null || this.#heliaNode === null) {
      throw new Error('Cannot subscribe to SIX — shared node not started');
    }

    if (this.#sixSubscriptions.has(sixId)) {
      return;
    }

    const onMessage = this.#onMessage;
    if (onMessage === null) {
      throw new Error('Cannot subscribe to SIX — onMessage handler not registered');
    }

    const cleanup = this.messagingService.setupSubscriptions(
      this.#libp2pNode,
      this.#heliaNode,
      onMessage,
      [SIX_TOPIC(sixId)]
    );

    this.#sixSubscriptions.set(sixId, cleanup);
    this.#sixConnectionCallbacks.set(sixId, onConnectionUpdate);

    if (this.#nodeMetadata !== null) {
      this.#nodeMetadata.status.subscribedTopics = [
        ...this.#nodeMetadata.status.subscribedTopics,
        SIX_TOPIC(sixId),
      ];
    }

    this.log.info(`Subscribed to SIX topic: ${SIX_TOPIC(sixId)}`);
  }

  async shutdownNode(): Promise<void> {
    await this.cleanupNode();
  }

  getLibp2pNode(): LibP2pWithServices | null {
    return this.#libp2pNode;
  }

  getHeliaNode(): HeliaNode | null {
    return this.#heliaNode;
  }

  getConnectionInfo(): ConnectionInfo[] {
    if (this.#libp2pNode === null) {
      return [];
    }

    return this.#libp2pNode.getConnections().map(extractConnectionInfo);
  }

  async getDiagnosticsSnapshot(brokerMultiaddr: string | null): Promise<P2PDiagnostics> {
    try {
      const libp2pNode = this.#libp2pNode;
      if (libp2pNode === null) {
        this.#pinnedCidsCache = null;
        return emptyP2PDiagnostics();
      }

      const peers = this.getConnectionInfo();

      let multiaddrs: string[] = [];
      try {
        multiaddrs = libp2pNode.getMultiaddrs().map((address) => address.toString());
      } catch {
        multiaddrs = [];
      }

      const pinnedCids = await this.readPinnedCids();

      return {
        peerCount: peers.length,
        peers,
        multiaddrs,
        pinCount: pinnedCids?.length ?? null,
        pinnedCids: pinnedCids ?? [],
        brokerStatus: deriveBrokerStatus(brokerMultiaddr, peers),
      };
    } catch {
      return emptyP2PDiagnostics();
    }
  }

  private async readPinnedCids(): Promise<string[] | null> {
    const heliaNode = this.#heliaNode;
    if (heliaNode === null) {
      this.#pinnedCidsCache = null;
      return null;
    }

    const cache = this.#pinnedCidsCache;
    if (
      cache !== null &&
      cache.node === heliaNode &&
      Date.now() - cache.at < P2P_PIN_COUNT_TTL_MS
    ) {
      return cache.value;
    }

    try {
      const pinnedCids: string[] = [];
      for await (const pin of heliaNode.helia.pins.ls()) {
        pinnedCids.push(pin.cid.toString());
      }
      this.#pinnedCidsCache = { value: pinnedCids, at: Date.now(), node: heliaNode };
      return pinnedCids;
    } catch {
      return null;
    }
  }

  reportBitswapStall(sixId: string): void {
    this.log.warn(`Bitswap stall reported for six '${sixId}' — no remediation action yet`);
  }

  private async doStartNode(options: SharedNodeOptions): Promise<SharedNodeStartResult> {
    try {
      this.#nodeMetadata = createInitialNodeMetadata(options.bootstrapNodes);

      this.#libp2pNode = await this.libp2pService.createNode({
        bootstrapNodes: options.bootstrapNodes,
        callbacks: this.createNodeCallbacks(),
        turnConfig: options.turnConfig,
      });

      this.#nodeMetadata.status.peerId = this.#libp2pNode.peerId.toString();

      this.#heliaNode = await this.heliaLifecycleService.createNode(this.#libp2pNode);

      const globalTopics = [ORG_GLOBAL_TOPIC, MESH_HEARTBEAT_TOPIC];
      if (options.userId !== undefined && options.userId.length > 0) {
        globalTopics.push(USER_TOPIC(options.userId));
      }

      const onMessage = this.#onMessage;
      if (onMessage === null) {
        throw new Error('onMessage handler lost before global topic subscription');
      }

      this.#globalTopicCleanup = this.messagingService.setupSubscriptions(
        this.#libp2pNode,
        this.#heliaNode,
        onMessage,
        globalTopics
      );

      this.#nodeMetadata.status.subscribedTopics = globalTopics;

      const peerId = this.#libp2pNode.peerId.toString();
      this.log.info(`Shared node started with peer ID: ${peerId}`);

      return { peerId, isSuccessful: true };
    } catch (error) {
      this.log.error('Failed to start shared node', error);
      await this.cleanupNode();
      throw error;
    }
  }

  private createNodeCallbacks(): LibP2pNodeCallbacks {
    return {
      onConnectionOpen: (info) => {
        if (this.#nodeMetadata === null) return;
        const { connectionStats } = this.#nodeMetadata.status;
        connectionStats.lifetimeStats.totalConnections++;
        if (info.isDirect) {
          connectionStats.lifetimeStats.directConnections++;
          connectionStats.uniquePeers.direct.add(info.peerId);
        } else {
          connectionStats.lifetimeStats.relayConnections++;
          connectionStats.uniquePeers.relay.add(info.peerId);
        }
        connectionStats.uniquePeers.total.add(info.peerId);
        this.updateConnectionsList();
      },
      onConnectionClose: (info) => {
        if (this.#nodeMetadata === null) return;
        const { connectionStats } = this.#nodeMetadata.status;
        connectionStats.lifetimeStats.totalDisconnections++;
        if (info.isDirect) {
          connectionStats.lifetimeStats.directDisconnections++;
        } else {
          connectionStats.lifetimeStats.relayDisconnections++;
        }
        this.updateConnectionsList();
      },
      onPeerConnect: (_peerId, connections) => {
        if (this.#nodeMetadata === null) return;
        this.#nodeMetadata.status.connections = connections;
        syncConnectionStats(this.#nodeMetadata.status, connections);
        this.broadcastConnectionUpdate();
      },
      onPeerDisconnect: () => {
        this.updateConnectionsList();
      },
      onSubscriptionChange: (_peerId, subscriptions) => {
        if (this.#nodeMetadata === null) return;
        const subscribedTopics = new Set(this.#nodeMetadata.status.subscribedTopics);
        for (const subscription of subscriptions) {
          if (subscription.subscribe) {
            subscribedTopics.add(subscription.topic);
          } else {
            subscribedTopics.delete(subscription.topic);
          }
        }
        this.#nodeMetadata.status.subscribedTopics = [...subscribedTopics];
      },
    };
  }

  private updateConnectionsList(): void {
    if (this.#libp2pNode === null || this.#nodeMetadata === null) return;

    const connections = this.#libp2pNode.getConnections().map(extractConnectionInfo);
    this.#nodeMetadata.status.connections = connections;
    this.#nodeMetadata.status.relayConnected = connections.some((c) => c.isRelay);
    syncConnectionStats(this.#nodeMetadata.status, connections);
    this.broadcastConnectionUpdate();
  }

  private broadcastConnectionUpdate(): void {
    for (const [, onConnectionUpdate] of this.#sixConnectionCallbacks) {
      onConnectionUpdate();
    }
  }

  ensureBootstrapNodesDialed(bootstrapNodes: string[]): void {
    if (this.#libp2pNode === null || this.#nodeMetadata === null) return;

    const known = new Set(this.#nodeMetadata.config.relayAddresses);
    for (const address of bootstrapNodes) {
      if (known.has(address)) continue;
      this.#nodeMetadata.config.relayAddresses.push(address);
      this.#libp2pNode.dial(multiaddr(address)).catch((error: unknown) => {
        const message = error instanceof Error ? error.message : String(error);
        this.log.debug(`Failed to dial new bootstrap peer ${address}: ${message}`);
      });
    }
  }

  private async cleanupNode(): Promise<void> {
    for (const cleanup of this.#sixSubscriptions.values()) {
      cleanup();
    }
    this.#sixSubscriptions.clear();
    this.#sixConnectionCallbacks.clear();

    this.#globalTopicCleanup?.();
    this.#globalTopicCleanup = null;

    if (this.#heliaNode !== null) {
      await this.heliaLifecycleService.destroyNode(this.#heliaNode);
      this.#heliaNode = null;
    }
    this.#pinnedCidsCache = null;

    if (this.#libp2pNode !== null) {
      await this.libp2pService.stopNode(this.#libp2pNode);
      this.#libp2pNode = null;
    }

    this.#nodeMetadata = null;
    this.#onMessage = null;
  }
}
